/**
 * WhatsNewModal — modal "Novidades" pós-login.
 *
 * Padrão inspirado em tmf-management: parseia `/CHANGELOG.md` (servido
 * como static pelo nginx), localStorage marca a última versão vista.
 * Modal abre automaticamente quando `APP_VERSION !== localStorage`.
 *
 * Também é acessível clicando na versão do footer (SidebarFooter abre
 * o modal manualmente).
 */

import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Bug, Wrench, Tag } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";

// ===== Types =====
interface ChangeItem {
  text: string;
  issueKeys: readonly string[];
}

interface ChangeSection {
  category: "added" | "fixed" | "changed" | "removed" | "other";
  label: string;
  items: ChangeItem[];
}

interface VersionEntry {
  version: string;
  date: string;
  summary: string;
  sections: ChangeSection[];
}

// ===== Constants =====
const STORAGE_KEY = "priceshow-whats-new-last-seen";

const ISSUE_PATTERN =
  /\[((?:TH|TMF9|CE|SUP|SU|ATG|WIZ|HCOR|FINA|TT|MOB|DC|S2|INFRA|PSHWO)-\d+|#\d+)\]/g;

const CATEGORY_CONFIG: Record<
  string,
  { label: string; tone: string; icon: typeof Sparkles; category: ChangeSection["category"] }
> = {
  Adicionado: {
    label: "Novidades",
    tone: "text-[var(--color-success)]",
    icon: Sparkles,
    category: "added",
  },
  Corrigido: {
    label: "Correções",
    tone: "text-[var(--color-destructive)]",
    icon: Bug,
    category: "fixed",
  },
  Alterado: {
    label: "Alterações",
    tone: "text-[var(--color-warning)]",
    icon: Wrench,
    category: "changed",
  },
  Removido: {
    label: "Removido",
    tone: "text-[var(--color-muted-foreground)]",
    icon: Wrench,
    category: "removed",
  },
  Infra: {
    label: "Infra",
    tone: "text-[var(--color-info)]",
    icon: Wrench,
    category: "other",
  },
  Validação: {
    label: "Validação",
    tone: "text-[var(--color-muted-foreground)]",
    icon: Tag,
    category: "other",
  },
};

// ===== Parse =====
function extractIssueKeys(text: string): string[] {
  const keys: string[] = [];
  let m;
  while ((m = ISSUE_PATTERN.exec(text)) !== null) keys.push(m[1]);
  ISSUE_PATTERN.lastIndex = 0;
  return keys;
}

function cleanText(text: string): string {
  // Remove **bold** simples do início pra deixar o texto mais limpo no
  // bullet. Não tenta parsear markdown completo — é overkill.
  return text.replace(/^\*\*([^*]+)\*\*:?\s*/, "$1: ").trim();
}

function parseChangelog(md: string): VersionEntry[] {
  const entries: VersionEntry[] = [];
  const lines = md.split("\n");
  let current: VersionEntry | null = null;
  let currentSection: ChangeSection | null = null;

  for (const line of lines) {
    const versionMatch = line.match(/^## \[([^\]]+)\]\s*-?\s*(.*)/);
    if (versionMatch) {
      const ver = versionMatch[1];
      if (!ver || ver === "Unreleased") {
        current = null;
        currentSection = null;
        continue;
      }
      current = {
        version: ver,
        date: versionMatch[2]?.trim() || "",
        summary: "",
        sections: [],
      };
      entries.push(current);
      currentSection = null;
      continue;
    }
    if (!current) continue;

    const sectionMatch = line.match(/^### (.+)/);
    if (sectionMatch) {
      const label = sectionMatch[1]!.trim();
      const cfg = CATEGORY_CONFIG[label];
      currentSection = {
        category: cfg?.category || "other",
        label,
        items: [],
      };
      current.sections.push(currentSection);
      continue;
    }

    if (line.startsWith("- ") && currentSection) {
      const raw = line.slice(2);
      currentSection.items.push({
        text: cleanText(raw),
        issueKeys: extractIssueKeys(raw),
      });
    } else if (
      line.startsWith("**") &&
      !current.summary &&
      !currentSection
    ) {
      // Linha-resumo logo após o cabeçalho da versão (negrito).
      current.summary = line.replace(/\*\*/g, "").trim();
    }
  }

  return entries;
}

// ===== Hook =====
export function useWhatsNew() {
  const current = __APP_VERSION__;
  const [lastSeen, setLastSeen] = useState<string | null>(null);

  useEffect(() => {
    setLastSeen(localStorage.getItem(STORAGE_KEY));
  }, []);

  const shouldShow =
    current !== "0.0.0" && lastSeen !== null && lastSeen !== current;
  return { shouldShow, currentVersion: current, lastSeen };
}

export function dismissWhatsNew() {
  localStorage.setItem(STORAGE_KEY, __APP_VERSION__);
}

// ===== Component =====
interface WhatsNewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Se true, mostra todas as últimas 5 versões; default só a atual. */
  showAll?: boolean;
}

export function WhatsNewModal({
  open,
  onOpenChange,
  showAll = false,
}: WhatsNewModalProps) {
  const { data: changelogMd } = useQuery({
    queryKey: ["changelog-whats-new"],
    queryFn: async () => {
      const resp = await fetch("/CHANGELOG.md", {
        credentials: "same-origin",
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.text();
    },
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const entries = useMemo(() => {
    if (!changelogMd) return [];
    return parseChangelog(changelogMd);
  }, [changelogMd]);

  const visibleEntries = useMemo(() => {
    if (showAll) return entries.slice(0, 5);
    const currentEntry = entries.find((e) => e.version === __APP_VERSION__);
    return currentEntry ? [currentEntry] : entries.slice(0, 1);
  }, [entries, showAll]);

  const handleOpenChange = (next: boolean) => {
    if (!next) dismissWhatsNew();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden p-0">
        <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-soft-strong)] px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/20 p-2 backdrop-blur-sm">
              <Sparkles className="h-6 w-6 text-[var(--color-primary-foreground)]" />
            </div>
            <div>
              <DialogTitle className="text-[var(--color-primary-foreground)]">
                Novidades · v{__APP_VERSION__}
              </DialogTitle>
              <DialogDescription className="text-[var(--color-primary-foreground)]/85">
                {visibleEntries[0]?.date
                  ? `Atualizado em ${visibleEntries[0].date}`
                  : "Última atualização"}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="flex max-h-[60vh] flex-col gap-6 overflow-y-auto px-6 py-5">
          {visibleEntries.length === 0 && (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Sem changelog disponível pra esta versão.
            </p>
          )}

          {visibleEntries.map((entry) => (
            <article key={entry.version} className="space-y-3">
              {visibleEntries.length > 1 && (
                <header className="border-b border-[var(--color-border)] pb-2">
                  <h3 className="font-mono text-sm font-semibold">
                    v{entry.version}
                  </h3>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {entry.date}
                  </p>
                </header>
              )}
              {entry.summary && (
                <p className="text-sm italic text-[var(--color-muted-foreground)]">
                  {entry.summary}
                </p>
              )}
              {entry.sections.map((section, idx) => {
                const cfg = CATEGORY_CONFIG[section.label] || {
                  label: section.label,
                  tone: "text-[var(--color-muted-foreground)]",
                  icon: Tag,
                  category: "other" as const,
                };
                const Icon = cfg.icon;
                return (
                  <section
                    key={`${entry.version}-${idx}`}
                    className="space-y-1.5"
                  >
                    <h4
                      className={cn(
                        "flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider",
                        cfg.tone,
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {cfg.label}
                    </h4>
                    <ul className="space-y-1.5 pl-5">
                      {section.items.map((item, i) => (
                        <li
                          key={i}
                          className="list-disc text-sm leading-relaxed text-[var(--color-foreground)]"
                        >
                          {item.text}
                          {item.issueKeys.length > 0 && (
                            <span className="ml-1 font-mono text-[10px] text-[var(--color-muted-foreground)]">
                              {item.issueKeys.map((k) => `[${k}]`).join(" ")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </article>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-border)] bg-[var(--color-table-row-tint)] px-6 py-3">
          <a
            href="/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[var(--color-muted-foreground)] underline hover:text-[var(--color-foreground)]"
          >
            Changelog completo
          </a>
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-[var(--color-primary-foreground)] hover:opacity-90"
          >
            Entendi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
